/**
 * Store: Product
 * ------------------------------------------------------------------------------
 * Global store for the the product object.
 * - Used for the PDP.
 *
 * @namespace useProductStore
 */
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'

import { storageNamespace } from '~/helpers/storage-namespaces'

export const useProductStore = defineStore({
  id: 'product',

  state: () => ({
    completeTheLook: [],
    currentVariant: {
      id: '',
      price: {},
      selectedOptions: [],
      title: '',
    },
    flexiFashion: false,
    initialProductHandle: mint.product?.handle || false,
    instanceId: uuidv4(),
    linkedProducts: [],
    productData: mint.product || false,
  }),

  actions: {

    /**
     * Query Shopify for a response.
     * @param {Object} payload - Object payload to pass in
     * @param {String} payload.handle - Valid Shopify product handle.
     * @param {String|Number} payload.variantId - Valid Shopify variant ID.
     * @param {Boolean|String} [payload.flexi] - String value for FlexiFashion query parameter.
     * @return {Object}
     */
    async queryProduct({ handle, variantId, flexi = false }) {
      try {
        const product = await mint.GetProduct({
          handle,
          flexi,
        })

        this.handleShopifyResponse({
          product: product,
          variantId,
        })

        return product

      } catch (error) {

        /**
         * Error is thrown in handleShopifyResponse() - no need to output anything.
         */
        return error
      }
    },

    /**
     * Handle the response from the API.
     * @param {Object} payload - payload from Attraqt API
     * @param {Object} payload.product - Formatted Product object.
     * @param {String|Number} payload.variantId - Initial variant ID to set.
     */
    handleShopifyResponse({ product, variantId }) {
      if (typeof product?.handle !== 'string') {
        mint.Log('error', new Error('Product handle is not defined'), { product, variantId })
      }

      if (!this.initialProductHandle) {
        this.initialProductHandle = product.handle
      }

      /**
       * Ensure each linked style has consistent swatch ordering.
       * - This is so that the visual order of the swatches is consistent
       *   any time the customer switches products via a swatch tap.
       * - Linked Styles are connected via Shopify Tags:
       *   (LinkedStyleReference:[ID]-[ID]-[ID])
       * - Each linked style product needs to have the exact same tag to become linked
       *
       * - Take the handle of the current PDP product and move it to first position.
       */
      const formattedProduct = product
      const completeTheLook = product.shopTheLook

      if (formattedProduct.linkedStyles.length) {
        const sortedSwatches = formattedProduct.linkedStyles.sort((left, right) =>
          left.handle.localeCompare(right.handle))

        const styleIndex = sortedSwatches.findIndex((style) => {
          return style.handle === this.initialProductHandle
        })

        sortedSwatches.unshift(
          sortedSwatches.splice(styleIndex, 1)[0],
        )

        formattedProduct.linkedStyles = sortedSwatches
      }

      this.setProduct({
        product: formattedProduct,
        variantId,
      })

      /**
       * Query STL *after* the main product has been set.
       * - STL is often off-screen
       * - Allows speed-up of initial page render as the main product is set.
       */
      const flexiFashionHandle = formattedProduct?.flexiFashion?.handle || false

      this.queryCompleteTheLook({
        stlProductIds: completeTheLook,
        flexiFashionHandle,
      })

      this.handleEventBusDispatch({
        productData: this.productData,
        currentVariant: this.currentVariant,
      })
    },

    /**
     * Query products from the shopTheLook metafield.
     * - 4 is a limit agreed with Trading, but any number will display
     * - Query for all products in metaShopTheLook and store in an array
     * - Once all STL products are returned, push them to product.completeTheLook
     *
     * @param {Object} data - Parameters.
     * @param {Array} data.stlProductIds - Array of product IDs to query
     * @param {Boolean|String} [data.flexiFashionHandle] - String value for FlexiFashion query parameter.
     */
    async queryCompleteTheLook({ stlProductIds, flexiFashionHandle = false }) {
      if (!stlProductIds) {
        return false
      }

      try {
        const ctlProductRequests = await stlProductIds.map((id) => {
          return mint.GetProduct({
            id,
            flexi: flexiFashionHandle,
          })
        })

        Promise.all(ctlProductRequests).then(ctlProducts => {
          const filteredProducts = ctlProducts.map((product) => {

            /**
             * Filter out the product if:
             * - It is the currently-viewed product
             * - It is restricted due to stock or market availability
             */
            if (
              !product?.handle ||
              (product.handle === this.productData.handle) ||
              product.restricted
            ) {
              return false
            }

            /**
             * For each STL product, set the marketing attribute as
             * that of the *parent* product - so that when products are
             * added to the basket via STL, they are attributed correctly.
             */
            if (this.flexiFashion) {
              product.attributes.flexiFashionName = this.flexiFashion.name
            }

            return product
          }).filter(Boolean)

          const ctl = {
            products: filteredProducts,
          }

          this.setProduct({
            completeTheLook: ctl,
            instanceId: 'INTERNAL',
          })

          this.handleEventBusDispatch({
            completeTheLook: this.completeTheLook,
          })
        })

      } catch (error) {
        mint.Log(
          'warning',
          `[PDP] Failed to fetch Shop The Look: ${this.productData.handle}`,
          error,
        )
      }
    },

    /**
     * Dispatch Event to other sections to update their states.
     */
    handleEventBusDispatch(changes) {

      /**
       * Convert ES6 Proxy objects to standard objects.
       */
      const payload = JSON.parse(JSON.stringify({
        ...changes,
        instanceId: this.instanceId,
        initialProduct: this.initialProductHandle,
      }))

      mint.EventBus.dispatch('mint:product:updated', payload)
    },

    /**
     * Update the current product on the PDP.
     * - Occurs on initial load or when a customer taps on a swatch.
     * @param {Object} data - Object payload to pass in.
     * @param {Object} data.variant - Formatted variant object
     * @param {String} data.linkedStyle - Shopify product handle.
     * @returns
     */
    async updateCurrentProduct({ variant = false, linkedStyle = false }) {
      let urlType = false

      try {
        // window.console.log('👗 Product Store: updateCurrentProduct', {
        //   variant, linkedStyle,
        // })

        /**
         * If the customer taps on the current swatch: do nothing.
         */
        if (linkedStyle === this.productData.handle) {
          // window.console.log('👗 Product Store: updateCurrentProduct - Same handle, do nothing', linkedStyle)
          return
        }

        /**
         * A variant object has been passed through - update page variant.
         */
        if (variant) {
          // window.console.log('👗 Product Store: updateCurrentProduct: variant', variant)

          urlType = 'variant'

          this.setProduct({
            variant: variant,
            instanceId: 'INTERNAL',
          })

          return
        }

        /**
         * The handle of a linked style has been passed in, so the customer
         * has tapped on a product swatch.
         * - Check to see if the full product object is stored already in the state
         * - If yes, replace the product object with the new product object.
         * - If no, query Shopify for the product and then replace it
         * - Add the new response to the existing array.
         */
        if (linkedStyle) {
          urlType = 'product'

          const storedProduct = this.linkedProducts.find((linked) => {
            return linked?.handle === linkedStyle
          })

          if (storedProduct) {
            // window.console.log('👗 Product Store: updateCurrentProduct - linkedStyle is cached')
            this.setProduct({
              product: storedProduct,
              instanceId: 'INTERNAL',
            })

            if (storedProduct.shopTheLook) {
              this.queryCompleteTheLook(storedProduct.shopTheLook)
            }

            return
          }

          // window.console.log('👗 Product Store: updateCurrentProduct - linkedStyle not cached')

          const response = await this.queryProduct({
            flexi: this.flexiFashion?.handle || false,
            handle: linkedStyle,
          })

          this.linkedProducts.push(response)
        }

      } catch (error) {
        mint.Log('error', '[PDP] Unable to fetch Linked Styles', error)

      } finally {
        this.updateCurrentURL({
          type: urlType,
        })
      }
    },

    /**
     * Update the current URL to reflect the new variant or Linked Style.
     * - If the variant has changed, append `variant` parameter.
     * - If Linked Style has changed, replace the handle.
     * @param {String} type - Type of URL change. `product` or `variant`
     */
    updateCurrentURL({ type } = false) {
      if (!this.productData.handle) {
        return
      }

      let url = mint.routes.root === '/'
        ? `//${location.host}/products/${this.productData.handle}`
        : `//${location.host}${mint.routes.root}/products/${this.productData.handle}`

      /**
       * Make a copy of the URL's current query parameters.
       * - Important for page view analytics (i.e. UTM)
       */
      const params = new URLSearchParams(document.location.search)

      if (type === 'variant' && !this.productData.onlyHasDefaultVariant) {
        params.set('variant', this.currentVariant?.id)
      }

      /**
       * Remove the variant parameter when switching between products
       */
      if (type === 'product') {
        params.delete('variant')
      }

      if (Array.from(params).length) {
        url += `?${params.toString()}`
      }

      // window.console.log('👗 Product Store: updateCurrentURL', { url })

      const newURL = `${window.location.protocol}${url}`

      if (window.location.href !== newURL) {
        history.replaceState({
          html: '',
        }, '', url)

        this.handleEventBusDispatch({
          productData: this.productData,
          currentVariant: this.currentVariant,
        })
      }
    },

    /**
     * Standalone method to update the objects in the global state.
     * @param {Object} product - Formatted Shopify product object.
     * @param {Object} variant - Formatted Shopify variant object.
     * @param {Object} variantId - Variant ID to use as override.
     * @param {Object} isSection - If the method is being called from a section.
     * @param {Object} initialProduct - Initial product handle.
     * @param {Object} completeTheLook - Complete The Look products.
     * @param {Object} instanceId - UUID for the section instance - prevents duplicate API calls.
     */
    setProduct({
      product,
      variant,
      variantId = false,
      isSection = false,
      initialProduct,
      completeTheLook,
      instanceId,
    }) {

      // window.console.log('👗 Product Store: setProduct: ⏱️ Setting', {
      //   product, variant, variantId, isSection,
      // })

      // We will get an instanceId only when coming through the event bus. If
      // the instanceId matches this one, we know it's an event this instance
      // dispatched and so we can ignore it
      if (instanceId && instanceId === this.instanceId) {
        return false
      }

      let matchingVariant = false

      /**
       * Initial product handle.
       * - First product queried on the PDP before swatch taps.
       */
      if (initialProduct) {
        this.initialProductHandle = initialProduct
      }

      /**
       * Product object has been passed in.
       * - Do not preselect a variant to ensure UX flow is consistent
       *   with products that contain variants.
       */
      if (product) {
        this.productData = product

        // if (product.onlyHasDefaultVariant) {
        //   matchingVariant = this.productData.variants[0]
        // }
      }

      /**
       * Variant object has been passed in.
       */
      if (variant) {
        matchingVariant = variant
      }

      /**
       * Variant ID has been passed in.
       */
      if (variantId) {
        matchingVariant = this.productData.variants.find((variant) => {
          return Number(variant.id) === Number(variantId)
        })
      }

      /**
       * Ensure a variant is always set.
       */
      if (matchingVariant) {
        this.currentVariant = matchingVariant

        // console.log('store: setProduct: matchingVariant', {
        //   variantId,
        //   variant,
        //   matchingVariant,
        // })
      }

      if (completeTheLook?.products && completeTheLook.products.length) {
        this.completeTheLook = completeTheLook
        this.productData.completeTheLook = completeTheLook
      }

      /**
       * Set the main FlexiFashion name in the state.
       */
      if (this.productData?.flexiFashion?.name) {
        this.flexiFashion = this.productData.flexiFashion
      }

      if (isSection) {
        return
      }

      const data = JSON.stringify({
        completeTheLook: this.completeTheLook,
        currentVariant: this.currentVariant,
        initialProduct: this.initialProductHandle,
        productData: this.productData,
      })

      sessionStorage.setItem(storageNamespace.pinia.product, data)
    },
  },
})
